import React from 'react';
import { Badge, Card, Form } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { TimelineStepEnum } from '../../../common/pokCore/validation/enums';

import { NoMediaplanSubItem } from './NoMediaplanSubItem';

export interface ChecklistItem {
  stepDefinitionId: string;
  text: string;
  completed: boolean;
  auto: boolean;
  type: TimelineStepEnum;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalInfo?: string[] | any;
}

interface StageModalChecklistProps {
  items: ChecklistItem[];
  loading?: boolean;
  onNoMediaplanChange?: (id: string, completed: boolean) => void;
}

export const StageModalChecklist: React.FC<StageModalChecklistProps> = ({
  items,
  loading,
  onNoMediaplanChange,
}) => {
  const { t, tk } = useTranslation('projectLng');
  const completedCount = items.filter(item => item.completed).length;

  const noMediaplanStep = items.find(
    item =>
      item.type === TimelineStepEnum.NoMediaplanForMediaProfileInCurrentMonth,
  );

  const displayItems = items.filter(
    item =>
      item.type !== TimelineStepEnum.NoMediaplanForMediaProfileInCurrentMonth,
  );

  const allCompleted = completedCount === items.length;
  const progressPercent =
    items.length > 0 ? (completedCount / items.length) * 100 : 0;

  return (
    <Card className="stage-modal-card stage-modal-card-checklist rounded-3">
      <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2 text-dark">
        <span>{t(tk.timeLine.checklist)}</span>
        <div className="ms-auto d-flex align-items-center gap-2">
          {allCompleted && <FaCheckCircle className="text-success" size={20} />}
          <Badge bg="secondary">
            {completedCount}/{items.length}
          </Badge>
        </div>
      </h6>

      <div className="stage-modal-checklist-progress">
        <div
          className="stage-modal-checklist-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {loading ? (
        <Skeleton count={3} className="mb-2" />
      ) : (
        <div className="d-flex flex-column gap-3">
          {displayItems.map(item => (
            <div key={item.stepDefinitionId}>
              <div
                className={`d-flex align-items-center rounded-2 stage-modal-checklist-item ${
                  item.completed
                    ? 'stage-modal-checklist-item-completed'
                    : 'stage-modal-checklist-item-pending'
                } ${item.auto ? 'stage-modal-checkbox-auto-wrapper' : ''}`}
              >
                <Form.Check
                  type="checkbox"
                  id={item.stepDefinitionId}
                  checked={item.completed}
                  className={`mb-0 ${item.auto ? 'stage-modal-checkbox-auto' : ''}`}
                  onChange={() =>
                    onNoMediaplanChange?.(
                      item.stepDefinitionId,
                      !item.completed,
                    )
                  }
                  disabled={item.auto && !item.completed}
                  label={
                    <span
                      className={`${
                        item.completed
                          ? 'text-decoration-line-through text-muted'
                          : ''
                      }`}
                    >
                      {item.text}
                      {item.auto ? (
                        <Badge className="ms-2" bg="success" pill>
                          {t(tk.timeLine.auto)}
                        </Badge>
                      ) : (
                        <Badge className="ms-2" bg="info" pill>
                          {t(tk.timeLine.manual)}
                        </Badge>
                      )}
                    </span>
                  }
                />
              </div>
              <NoMediaplanSubItem
                itemType={item.type}
                itemCompleted={item.completed}
                noMediaplanStep={noMediaplanStep}
                onChange={onNoMediaplanChange}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
